/**
 * PDF « Synthèse Double Diplôme » : document A4 sobre pour la direction et le
 * partenaire ISPI. En-tête à deux logos (Institut International 2i + ISPI).
 *
 * Contenu :
 *  - synthèse par option et par niveau (effectif, moyennes FI / DD / générale) ;
 *  - détail par étudiant (moyenne formation initiale, moyenne DD, moyenne
 *    générale = (FI + DD) / 2, mention).
 *
 * Le logo ISPI est le MÊME fichier que les relevés DD (/ispi-logo.jpg) ; s'il
 * est absent, un bloc texte « ISPI » est affiché à la place.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { SyntheseDDResponse, SyntheseDDStudent, SyntheseDDAgg } from '../../services/resultatsApi';

type RGB = [number, number, number];

const ENCRE: RGB = [30, 41, 59];
const GRIS: RGB = [100, 116, 139];
const FILET: RGB = [203, 213, 225];
const FOND_ENTETE: RGB = [241, 245, 249];
const ACCENT: RGB = [30, 58, 95];

const fmtMoy = (n: number | null | undefined): string =>
  (n === undefined || n === null) ? '—' : n.toFixed(2);
const fmtInt = (n: number | undefined | null): string =>
  (n === undefined || n === null) ? '-' : String(n);

export function safeTxt(s: string | number | undefined | null): string {
  if (s === undefined || s === null) return '';
  return String(s)
    .replace(/→/g, '->').replace(/÷/g, '/').replace(/×/g, 'x')
    .replace(/…/g, '...').replace(/[“”«»]/g, '"').replace(/[‘’]/g, "'")
    .replace(/—/g, '-').replace(/–/g, '-').replace(/≥/g, '>=').replace(/≤/g, '<=')
    .replace(/[  ]/g, ' ');
}

// Charge une image et la renvoie en PNG base64. On passe systématiquement par
// un <canvas> : jsPDF.addImage ne sait PAS rendre les JPEG progressifs (le logo
// ISPI /ispi-logo.jpg en est un → addImage('JPEG') lève et l'image disparaît,
// alors que le même fichier s'affiche très bien en <img> sur le relevé DD). Le
// canvas décode (le navigateur gère le progressif) et ré-encode en PNG, rendu
// fiable par jsPDF. Même logo que les relevés DD, affiché à l'identique.
async function chargerLogoBase64(src: string): Promise<string | null> {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.size) return null;
    const url = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch { return null; }
}

export interface OptionsPdfSyntheseDD {
  data: SyntheseDDResponse;
  perimetre: { pole?: string; niveau?: string; option?: string };
  institut?: { ville?: string; signataireTitre?: string; signataireNom?: string };
  nomFichier: string;
}

export async function genererPdfSyntheseDD(o: OptionsPdfSyntheseDD): Promise<void> {
  const { data } = o;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const MX = 16;
  const annee = data.annee || '';
  const dateFr = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  const texte = (
    t: string, x: number, yy: number,
    opt: { taille?: number; gras?: boolean; italique?: boolean; couleur?: RGB; align?: 'left' | 'center' | 'right' } = {},
  ) => {
    const style = opt.gras ? (opt.italique ? 'bolditalic' : 'bold') : (opt.italique ? 'italic' : 'normal');
    doc.setFont('helvetica', style);
    doc.setFontSize(opt.taille ?? 9);
    doc.setTextColor(...(opt.couleur ?? ENCRE));
    doc.text(safeTxt(t), x, yy, { align: opt.align ?? 'left' });
  };
  const filet = (yy: number, epaisseur = 0.2, couleur: RGB = FILET, x1 = MX, x2 = W - MX) => {
    doc.setDrawColor(...couleur); doc.setLineWidth(epaisseur); doc.line(x1, yy, x2, yy);
  };
  const finTableau = (): number => (doc as any).lastAutoTable.finalY;
  const BAS = H - 20;
  let y = 0;
  const assurer = (besoin: number) => { if (y + besoin > BAS) { doc.addPage(); y = 20; } };
  let numSection = 0;
  const titreSection = (t: string) => {
    assurer(16); numSection += 1;
    texte(`${numSection}. ${t}`, MX, y, { taille: 10.5, gras: true, couleur: ACCENT });
    filet(y + 2, 0.25); y += 6.5;
  };

  const baseTable = {
    theme: 'grid' as const,
    styles: { font: 'helvetica', fontSize: 8, textColor: ENCRE, lineColor: FILET, lineWidth: 0.15, cellPadding: 1.6, valign: 'middle' as const },
    headStyles: { fillColor: FOND_ENTETE, textColor: ENCRE, fontStyle: 'bold' as const, halign: 'center' as const },
    footStyles: { fillColor: FOND_ENTETE, textColor: ENCRE, fontStyle: 'bold' as const },
    bodyStyles: { fillColor: [255, 255, 255] as RGB },
    margin: { left: MX, right: MX, top: 20, bottom: H - BAS },
  };

  // ─── En-tête à deux logos (2i à gauche, partenaire ISPI à droite) ────────
  const [logo2i, logoIspi] = await Promise.all([
    chargerLogoBase64('/logo.png'), chargerLogoBase64('/ispi-logo.jpg'),
  ]);
  if (logo2i) { try { doc.addImage(logo2i, 'PNG', MX, 9, 15, 15); } catch { /* facultatif */ } }
  texte('INSTITUT INTERNATIONAL 2i', logo2i ? MX + 18 : MX, 15, { taille: 10.5, gras: true });
  texte('Instruire pour Mieux Servir', logo2i ? MX + 18 : MX, 20, { taille: 8, italique: true, couleur: GRIS });
  // Logo ISPI (image large ~2.49:1) à droite ; repli texte si l'image manque.
  if (logoIspi) {
    const wL = 25, hL = 10;
    try { doc.addImage(logoIspi, 'PNG', W - MX - wL, 9, wL, hL); } catch { /* facultatif */ }
    texte('Partenaire Double Diplôme', W - MX, 22.5, { taille: 7.5, italique: true, couleur: GRIS, align: 'right' });
  } else {
    doc.setDrawColor(...GRIS); doc.setLineWidth(0.3); doc.rect(W - MX - 15, 9, 15, 15);
    texte('ISPI', W - MX - 7.5, 17.5, { taille: 10, gras: true, couleur: GRIS, align: 'center' });
    texte('Partenaire Double Diplôme', W - MX, 22.5, { taille: 7.5, italique: true, couleur: GRIS, align: 'right' });
  }
  filet(27, 0.5, ENCRE);

  texte('SYNTHÈSE DU DOUBLE DIPLÔME', W / 2, 37, { taille: 14, gras: true, align: 'center' });
  texte('Moyennes de la formation initiale et du cursus double diplôme', W / 2, 43, { taille: 9, couleur: GRIS, align: 'center' });

  // Périmètre
  const { pole, niveau, option } = o.perimetre;
  const perim = [
    pole ? `Pôle ${pole}` : 'Tous les pôles',
    niveau ? `Niveau ${niveau}` : 'Tous les niveaux',
    option ? `Option ${option}` : 'Toutes les options',
  ].join('  ·  ');
  autoTable(doc, {
    startY: 49,
    body: [
      ['Périmètre', safeTxt(perim)],
      ['Effectif Double Diplôme', `${data.effectif} étudiant(s)`],
    ],
    theme: 'plain', margin: { left: MX, right: MX },
    styles: { font: 'helvetica', fontSize: 8.5, textColor: ENCRE, cellPadding: { top: 0.7, bottom: 0.7, left: 0, right: 2 } },
    columnStyles: { 0: { cellWidth: 46, textColor: GRIS } },
  });
  if (annee) texte(`Année académique ${annee}`, W - MX, 54, { taille: 8.5, couleur: GRIS, align: 'right' });
  y = finTableau() + 6;

  // ─── 1. Synthèse par option ─────────────────────────────────────────────
  const aggTable = (titre: string, col1: string, lignes: SyntheseDDAgg[]) => {
    if (!lignes.length) return;
    titreSection(titre);
    autoTable(doc, {
      ...baseTable, startY: y,
      head: [[col1, 'Effectif', 'Moy. FI', 'Moy. DD', 'Moy. Générale', 'Admis (>= 10)']],
      body: lignes.map(l => [
        safeTxt(l.libelle), fmtInt(l.effectif), fmtMoy(l.moyenne_fi),
        fmtMoy(l.moyenne_dd), fmtMoy(l.moyenne_generale), fmtInt(l.nb_admis),
      ]),
      columnStyles: {
        0: { halign: 'left' }, 1: { halign: 'right', cellWidth: 20 },
        2: { halign: 'right', cellWidth: 24 }, 3: { halign: 'right', cellWidth: 24 },
        4: { halign: 'right', cellWidth: 28, fontStyle: 'bold' }, 5: { halign: 'right', cellWidth: 26 },
      },
      didParseCell: (d) => { if (d.section === 'head' && d.column.index === 0) d.cell.styles.halign = 'left'; },
    });
    y = finTableau() + 5;
  };
  aggTable('Synthèse par option', 'Option', data.par_option);
  aggTable('Synthèse par niveau', 'Niveau', data.par_niveau);

  // ─── 3. Détail par étudiant ─────────────────────────────────────────────
  titreSection('Détail par étudiant');
  const etu: SyntheseDDStudent[] = data.students;
  autoTable(doc, {
    ...baseTable, startY: y,
    styles: { ...baseTable.styles, fontSize: 7.4, cellPadding: 1.3 },
    head: [['Matricule', 'Nom et prénoms', 'Niv.', 'Option', 'Moy. FI', 'Moy. DD', 'Moy. Gén.', 'Mention']],
    body: etu.map(s => [
      safeTxt(s.matricule), safeTxt(`${s.nom} ${s.prenoms}`.trim()), safeTxt(s.niveau),
      safeTxt(s.option), fmtMoy(s.moyenne_fi), fmtMoy(s.moyenne_dd),
      fmtMoy(s.moyenne_generale), safeTxt(s.mention),
    ]),
    columnStyles: {
      0: { cellWidth: 22 }, 1: { halign: 'left', cellWidth: 44 }, 2: { halign: 'center', cellWidth: 10 },
      3: { halign: 'left' }, 4: { halign: 'right', cellWidth: 15 }, 5: { halign: 'right', cellWidth: 15 },
      6: { halign: 'right', cellWidth: 16, fontStyle: 'bold' }, 7: { halign: 'left', cellWidth: 20 },
    },
    didParseCell: (d) => {
      if (d.section === 'head') d.cell.styles.halign = [1, 3, 7].includes(d.column.index) ? 'left' : 'center';
    },
  });
  y = finTableau() + 4;
  doc.setFont('helvetica', 'italic'); doc.setFontSize(7.5); doc.setTextColor(...GRIS);
  const noteTxt = doc.splitTextToSize(safeTxt(
    'Moyenne FI : moyenne du semestre de formation initiale. Moyenne DD : moyenne arithmétique des '
    + 'modules du cursus double diplôme. Moyenne générale = (Moyenne FI + Moyenne DD) / 2 ; « — » quand '
    + "l'une des deux moyennes n'est pas encore disponible."), W - 2 * MX);
  assurer(noteTxt.length * 3.4 + 2);
  doc.text(noteTxt, MX, y); y += noteTxt.length * 3.4 + 3;

  // ─── Visa ───────────────────────────────────────────────────────────────
  assurer(34);
  y += 4;
  const ville = o.institut?.ville;
  texte(ville ? `Fait à ${ville}, le ${dateFr}` : `Fait le ${dateFr}`, W - MX, y, { taille: 9, align: 'right' });
  if (o.institut?.signataireTitre) texte(o.institut.signataireTitre, W - MX, y + 6, { taille: 9, gras: true, align: 'right' });
  if (o.institut?.signataireNom) texte(o.institut.signataireNom, W - MX, y + 26, { taille: 9, gras: true, align: 'right' });

  // ─── Pied de page ───────────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    if (p > 1) {
      texte('INSTITUT INTERNATIONAL 2i × ISPI', MX, 12, { taille: 7.5, gras: true, couleur: GRIS });
      texte(`Synthèse Double Diplôme${annee ? ` · ${annee}` : ''}`, W - MX, 12, { taille: 7.5, couleur: GRIS, align: 'right' });
      filet(14.5, 0.2);
    }
    filet(H - 14, 0.2);
    texte('Institut International 2i · Cursus Double Diplôme avec ISPI', MX, H - 9, { taille: 7.5, couleur: GRIS });
    texte(`Page ${p} sur ${totalPages}`, W - MX, H - 9, { taille: 7.5, couleur: GRIS, align: 'right' });
  }
  doc.save(o.nomFichier);
}
